import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { parsePln, plnToInput } from '../catalog/money';
import { CATALOG_KEY, categoryLabels, KIND_LABELS, useCategories, type Kind } from '../catalog/ItemForm';
import { NumberInput } from '../ui/NumberInput';
import { DuplicateNameHint } from '../catalog/DuplicateNameHint';
import { DialogLayer } from '../ui/Dialog';
import { sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

/** What the quote knows when it asks for a new catalog item: the own line's (or the typed) name, its section's category, its price. */
export type NewItemSeed = { name: string; categoryId: string | null; priceGrosze: number | null };

/**
 * A small "Dodaj do katalogu" dialog (P8, point 27, note 22:50): name, category, rental price per day and kind — the
 * rest is filled in the catalog later (the orange dot shows what is missing). A quantity item starts with 0 pieces.
 * Calls `onCreated` with the new item's id; the quote then puts it in its line.
 */
export function NewCatalogItemDialog({ seed, onCreated, onClose }: { seed: NewItemSeed; onCreated: (itemId: string) => Promise<void>; onClose: () => void }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const categories = useCategories(!!me && can(me, 'catalog.categories', 'view')).data?.categories ?? [];
  const [name, setName] = useState(seed.name);
  const [categoryId, setCategoryId] = useState(seed.categoryId ?? '');
  const [price, setPrice] = useState(seed.priceGrosze !== null ? plnToInput(seed.priceGrosze) : '');
  const [kind, setKind] = useState<Kind>('bulk');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // rule 7a: a field the role may not see is neither shown nor sent
  const seesPrice = !!me && fieldVisible(me, 'catalog.items', 'rentalPriceDayGrosze');

  /** Adds the item; false when it did not (the reason is shown). */
  async function persist(): Promise<boolean> {
    if (name.trim() === '') return (setError('Podaj nazwę'), false);
    let rentalPriceDayGrosze: number | undefined;
    if (seesPrice && price.trim() !== '') {
      try {
        rentalPriceDayGrosze = parsePln(price);
      } catch (err) {
        return (setError((err as Error).message), false);
      }
    }
    setBusy(true);
    try {
      const item = await api<{ id: string }>('/api/catalog/items', {
        method: 'POST',
        body: { name: name.trim(), kind, categoryId: categoryId || null, ...(kind === 'serialized' ? {} : { quantityOwned: 0 }), ...(rentalPriceDayGrosze !== undefined ? { rentalPriceDayGrosze } : {}) },
      });
      void queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
      await onCreated(item.id);
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? (fieldIssues(err)[0]?.message ?? err.message) : 'Nie udało się dodać do katalogu');
      setBusy(false);
      return false;
    }
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void persist();
  }
  // owner 2026-10-23 21:14: Escape or leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const opening = useOpening({ name, categoryId, price, kind });
  const guardRef = useUnsavedGuard({ dirty: !sameValues({ name, categoryId, price, kind }, opening), save: () => persist() });

  return (
    <div className="dialog-backdrop">
      <form ref={guardRef} className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="new-item-title" onSubmit={submit} data-feature-id="quotes.add_to_catalog">
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="new-item-title">Dodaj do katalogu</h2>
        <p className="muted">Pozycja trafi do katalogu sprzętu, a wiersz kosztorysu stanie się tą pozycją (z tą samą ceną). Resztę uzupełnisz w katalogu.</p>
        <label className="field">
          <span>Nazwa</span>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" required />
        </label>
        {/* P14: the name is already in the catalog: the line can take that item instead of a new one */}
        <DuplicateNameHint
          name={name}
          actions={(found) => [
            {
              label: 'Użyj istniejącej',
              onClick: () => {
                setBusy(true);
                void onCreated(found.id).catch(() => setBusy(false));
              },
            },
          ]}
        />
        {categories.length > 0 && (
          <label className="field">
            <span>Kategoria</span>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">Bez kategorii</option>
              {categoryLabels(categories).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {seesPrice && (
          <label className="field">
            <span>Cena wynajmu za dobę (zł)</span>
            <NumberInput kind="money" value={price} onValueChange={setPrice} />
          </label>
        )}
        <label className="field">
          <span>Rodzaj</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
            {(Object.keys(KIND_LABELS) as Kind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </label>
        {/* stage 2 (F28): "Katalog dostawcy" — an item of a rental partner's catalog will be a choice here */}
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={busy}>
            Dodaj do katalogu
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
